import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { getLocale, QUERY_KEYS } from '@libs/constants';
import { nextApiCall } from '@libs/nextApiCall';
import { nextApiCallErrorFn } from '@libs/nextApiCallErrorFn';
import { getUniqueAssociatedProductIds } from '@libs/product-associations.helper';
import type { AssociationProductFragmentEnhanced } from '@models/enhanced-fragments';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { ToastType, useToasts } from '@hooks/useToasts';
import { useTranslation } from 'next-i18next';

export const useUniqueAssociatedProductsQuery = () => {
  const router = useRouter();
  const locale = getLocale(router);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const { t } = useTranslation(['layout/header', 'toast/toast']);
  const { addToast } = useToasts();

  return useQuery<Record<string, AssociationProductFragmentEnhanced>>(
    [QUERY_KEYS.ASSOCIATION_PRODUCTS, currentProduct.id, locale],
    async () => {
      const productIds = getUniqueAssociatedProductIds([currentProduct]);

      if (productIds.length === 0) {
        return {};
      }

      const response = await nextApiCall('unique-associated-products', { productIds, locale });

      if (!response.ok) {
        throw new Error(`Failed to fetch associated products for ${currentProduct.id}`);
      }

      return (await response.json()) as Record<string, AssociationProductFragmentEnhanced>;
    },
    {
      onError: err => {
        addToast({ content: t('TOAST.ERROR_OCCURRED', { ns: 'toast/toast' }), type: ToastType.ERROR });
        nextApiCallErrorFn(err, `Fetch associated products for ${currentProduct.id} failed`);
      },
    }
  );
};
